import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

import { Bouton, Champ, Liste, Pastille, Saisie } from '../components/ui/Base'
import { Bandeau, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import { ApiError } from '../lib/api/erreurs'
import type { Utilisateur } from '../lib/api/types'

/**
 * Création et modification d'un infirmier (§5.2).
 *
 * ============================================================================
 * VOCABULAIRE LIBÉRAL, SANS EXCEPTION.
 *
 * Un infirmier libéral n'est pas un salarié : ni « salaire », ni « embauche »,
 * ni « contrat de travail ». On saisit un **mode de rémunération** — horaire ou
 * rétrocession — et le mot compte autant que le calcul : le vocabulaire salarié
 * couplé à un planning imposé est précisément le triptyque de la
 * requalification prud'homale.
 * ============================================================================
 */

const commun = {
  email: z.string().email('Adresse électronique invalide.'),
  nom: z.string().min(1, 'Le nom est obligatoire.').max(100),
  prenom: z.string().min(1, 'Le prénom est obligatoire.').max(100),
  telephone: z
    .string()
    .regex(/^(?:\+33|0)\s?[1-9](?:[\s.-]?\d{2}){4}$/, 'Numéro de téléphone invalide.')
    .or(z.literal('')),
  role: z.enum(['TITULAIRE', 'COLLABORATEUR', 'REMPLACANT']),
  statut: z.enum(['ACTIF', 'INACTIF']),
  numero_adeli: z.string().regex(/^\d{9}$/, "Le numéro ADELI comporte 9 chiffres.").or(z.literal('')),
  numero_rpps: z.string().regex(/^\d{11}$/, 'Le numéro RPPS comporte 11 chiffres.').or(z.literal('')),
  siret: z.string().regex(/^\d{14}$/, 'Le SIRET comporte 14 chiffres.').or(z.literal('')),
  couleur: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Une couleur au format #RRGGBB.'),
  adresse_depart: z.string().optional(),
  mot_de_passe: z.string().optional(),
}

/**
 * ============================================================================
 * LE SCHÉMA EST UN `discriminatedUnion`, PAS UN OBJET À CHAMPS OPTIONNELS.
 *
 * Le §5.2 pose la règle : `taux_horaire` est requis si et seulement si le mode
 * est `HORAIRE`, `taux_retrocession` si et seulement si le mode est
 * `RETROCESSION`. Avec deux champs simplement optionnels, on peut enregistrer
 * un infirmier sans aucun taux — et sa facturation ne calcule plus rien, sans
 * qu'aucun message ne l'ait annoncé.
 *
 * L'union rend l'état incohérent **inexprimable** : le champ non pertinent
 * n'est ni exigé, ni envoyé.
 * ============================================================================
 */
const schema = z.discriminatedUnion('mode_remuneration', [
  z.object({
    ...commun,
    mode_remuneration: z.literal('HORAIRE'),
    taux_horaire: z.coerce
      .number({ invalid_type_error: 'Le taux horaire est obligatoire en mode horaire.' })
      .gt(0, 'Le taux horaire doit être positif.'),
  }),
  z.object({
    ...commun,
    mode_remuneration: z.literal('RETROCESSION'),
    taux_retrocession: z.coerce
      .number({ invalid_type_error: 'Le taux de rétrocession est obligatoire en mode rétrocession.' })
      .min(0, 'Le taux ne peut pas être négatif.')
      .max(100, 'Le taux est un pourcentage : 100 au maximum.'),
  }),
])

type Valide = z.infer<typeof schema>

/**
 * Vue **à plat** pour React Hook Form.
 *
 * `register('taux_horaire')` ne se type pas contre une union : le champ
 * n'existe que dans une des branches. Le formulaire manipule donc les deux
 * champs en permanence, et c'est le schéma qui tranche à la validation — le
 * champ masqué n'est jamais lu.
 */
interface Brouillon {
  email: string
  nom: string
  prenom: string
  telephone: string
  role: 'TITULAIRE' | 'COLLABORATEUR' | 'REMPLACANT'
  statut: 'ACTIF' | 'INACTIF'
  numero_adeli: string
  numero_rpps: string
  siret: string
  couleur: string
  adresse_depart: string
  mot_de_passe: string
  mode_remuneration: 'HORAIRE' | 'RETROCESSION'
  taux_horaire: string
  taux_retrocession: string
}

const VALEURS_INITIALES: Brouillon = {
  email: '',
  nom: '',
  prenom: '',
  telephone: '',
  role: 'COLLABORATEUR',
  statut: 'ACTIF',
  numero_adeli: '',
  numero_rpps: '',
  siret: '',
  couleur: '#0ea5e9',
  adresse_depart: '',
  mot_de_passe: '',
  mode_remuneration: 'HORAIRE',
  taux_horaire: '',
  taux_retrocession: '',
}

/** Palette lisible sur fond clair, et distinguable en pastille de 12 px. */
const COULEURS = ['#0ea5e9', '#16a34a', '#dc2626', '#9333ea', '#ea580c', '#0d9488', '#c026d3', '#65a30d']

export function UtilisateurFormulaire() {
  const { id } = useParams()
  const modification = Boolean(id)
  const naviguer = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()

  const requete = useQuery({
    queryKey: cles.utilisateurs.detail(id ?? ''),
    queryFn: ({ signal }) => api.get<Utilisateur>(`/utilisateurs/${id}`, signal),
    enabled: modification,
  })

  const { register, handleSubmit, reset, watch, setValue, formState, setError } = useForm<Brouillon>({
    // Le schéma valide l'union ; le formulaire, lui, reste plat. Le transtypage
    // ne masque aucun risque : les deux décrivent le même jeu de champs, à la
    // discrimination près.
    resolver: zodResolver(schema) as unknown as Resolver<Brouillon>,
    defaultValues: VALEURS_INITIALES,
  })

  const mode = watch('mode_remuneration')
  const couleur = watch('couleur')

  useEffect(() => {
    const u = requete.data
    if (!u) return
    reset({
      ...VALEURS_INITIALES,
      email: u.email,
      nom: u.nom,
      prenom: u.prenom,
      telephone: u.telephone ?? '',
      role: u.role,
      statut: u.statut,
      numero_adeli: u.numero_adeli ?? '',
      numero_rpps: u.numero_rpps ?? '',
      siret: u.siret ?? '',
      couleur: u.couleur,
      adresse_depart: u.adresse_depart ?? '',
      mode_remuneration: u.mode_remuneration,
      taux_horaire: u.taux_horaire ?? '',
      taux_retrocession: u.taux_retrocession ?? '',
    })
  }, [requete.data, reset])

  const enregistrer = useMutation({
    mutationFn: (v: Brouillon) => {
      const corps: Record<string, unknown> = {
        email: v.email.toLowerCase().trim(),
        nom: v.nom.trim(),
        prenom: v.prenom.trim(),
        telephone: v.telephone.trim() || null,
        role: v.role,
        statut: v.statut,
        numero_adeli: v.numero_adeli.trim() || null,
        numero_rpps: v.numero_rpps.trim() || null,
        siret: v.siret.trim() || null,
        couleur: v.couleur,
        adresse_depart: v.adresse_depart.trim() || null,
        mode_remuneration: v.mode_remuneration,
        // Le champ non pertinent part explicitement à `null` : le laisser
        // à son ancienne valeur ferait cohabiter un taux horaire et un taux de
        // rétrocession, et R4 n'aurait plus de source unique.
        taux_horaire: v.mode_remuneration === 'HORAIRE' ? Number(v.taux_horaire).toFixed(2) : null,
        taux_retrocession:
          v.mode_remuneration === 'RETROCESSION' ? Number(v.taux_retrocession).toFixed(2) : null,
      }
      if (!modification) corps.mot_de_passe = v.mot_de_passe

      return modification
        ? api.patch<Utilisateur>(`/utilisateurs/${id}`, corps)
        : api.post<Utilisateur>('/utilisateurs', corps)
    },
    onSuccess: () => {
      toast(modification ? 'Fiche modifiée.' : 'Infirmier ajouté au cabinet.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.utilisateurs.tout })
      // Le planning affiche la pastille et le nom de chaque infirmier : un
      // changement de couleur ou de statut doit s'y voir immédiatement.
      void queryClient.invalidateQueries({ queryKey: cles.planning.tout })
      naviguer('/utilisateurs')
    },
    onError: (erreur) => {
      if (erreur instanceof ApiError && erreur.champ && erreur.champ in VALEURS_INITIALES) {
        setError(erreur.champ as keyof Brouillon, { message: erreur.message })
        return
      }
      toast(erreur instanceof Error ? erreur.message : 'Enregistrement impossible.', 'erreur')
    },
  })

  if (modification && requete.isPending) return <Squelette lignes={10} />
  if (modification && requete.isError) {
    return <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
  }

  // `formState.errors` est typé contre `Brouillon` ; les messages de l'union
  // arrivent bien sur les mêmes noms de champ.
  const e = formState.errors as Partial<Record<keyof Valide | keyof Brouillon, { message?: string }>>

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <Link to="/utilisateurs" className="text-sm text-sky-700 hover:underline">
          ← Infirmiers
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-slate-900">
          {modification ? `Modifier ${requete.data?.prenom} ${requete.data?.nom}` : 'Nouvel infirmier'}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit((v) => enregistrer.mutate(v))}
        className="space-y-6 rounded-lg border border-slate-200 bg-white p-5"
        noValidate
      >
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Identité</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ libelle="Nom" id="nom" erreur={e.nom?.message} obligatoire>
              <Saisie id="nom" autoComplete="family-name" {...register('nom')} />
            </Champ>
            <Champ libelle="Prénom" id="prenom" erreur={e.prenom?.message} obligatoire>
              <Saisie id="prenom" autoComplete="given-name" {...register('prenom')} />
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ libelle="Adresse électronique" id="email" erreur={e.email?.message} obligatoire>
              <Saisie id="email" type="email" autoComplete="email" {...register('email')} />
            </Champ>
            <Champ libelle="Téléphone" id="tel" erreur={e.telephone?.message}>
              <Saisie id="tel" type="tel" {...register('telephone')} />
            </Champ>
          </div>

          {!modification ? (
            <>
              <Champ
                libelle="Mot de passe initial"
                id="mdp"
                erreur={e.mot_de_passe?.message}
                aide="Communiqué de vive voix. Le changement est imposé à la première connexion."
              >
                <Saisie id="mdp" type="text" autoComplete="new-password" {...register('mot_de_passe')} />
              </Champ>
              <Bandeau ton="attention">
                Ce mot de passe transite par un canal que le cabinet ne maîtrise pas — un SMS,
                une note. L&apos;infirmier devra en choisir un autre dès sa première connexion.
              </Bandeau>
            </>
          ) : null}
        </section>

        <section className="space-y-4 border-t border-slate-100 pt-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Rôle et identifiants professionnels
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ
              libelle="Rôle"
              id="role"
              aide="Le titulaire seul accède aux réglages, aux fiches et au journal d'audit."
            >
              <Liste id="role" {...register('role')}>
                <option value="TITULAIRE">Titulaire</option>
                <option value="COLLABORATEUR">Collaborateur</option>
                <option value="REMPLACANT">Remplaçant</option>
              </Liste>
            </Champ>
            <Champ libelle="Statut" id="statut" aide="Un infirmier inactif reste dans l'historique.">
              <Liste id="statut" {...register('statut')}>
                <option value="ACTIF">Actif</option>
                <option value="INACTIF">Inactif</option>
              </Liste>
            </Champ>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Champ libelle="Numéro ADELI" id="adeli" erreur={e.numero_adeli?.message} aide="9 chiffres">
              <Saisie id="adeli" inputMode="numeric" {...register('numero_adeli')} />
            </Champ>
            <Champ libelle="Numéro RPPS" id="rpps" erreur={e.numero_rpps?.message} aide="11 chiffres">
              <Saisie id="rpps" inputMode="numeric" {...register('numero_rpps')} />
            </Champ>
            <Champ libelle="SIRET" id="siret" erreur={e.siret?.message} aide="14 chiffres">
              <Saisie id="siret" inputMode="numeric" {...register('siret')} />
            </Champ>
          </div>
          <Champ
            libelle="Adresse de départ"
            id="depart"
            aide="Point de départ des tournées. À défaut, l'adresse du cabinet est utilisée."
          >
            <Saisie id="depart" {...register('adresse_depart')} />
          </Champ>

          <Champ libelle="Couleur au planning" id="couleur" erreur={e.couleur?.message}>
            <div className="flex flex-wrap items-center gap-2">
              {COULEURS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setValue('couleur', c, { shouldDirty: true })}
                  aria-label={`Couleur ${c}`}
                  aria-pressed={couleur === c}
                  className={`h-8 w-8 rounded-full ring-offset-2 transition ${
                    couleur === c ? 'ring-2 ring-slate-900' : 'ring-1 ring-black/10'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <Saisie id="couleur" type="color" className="h-9 w-16 p-1" {...register('couleur')} />
              <span className="flex items-center gap-1.5 text-sm text-slate-600">
                <Pastille couleur={couleur} />
                {couleur}
              </span>
            </div>
          </Champ>
        </section>

        {/* ── Rémunération ── */}
        <section className="space-y-4 border-t border-slate-100 pt-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Rémunération
          </h2>
          <Champ libelle="Mode" id="mode">
            <Liste id="mode" {...register('mode_remuneration')}>
              <option value="HORAIRE">Horaire — un montant par heure travaillée</option>
              <option value="RETROCESSION">
                Rétrocession — un pourcentage des honoraires revient au cabinet
              </option>
            </Liste>
          </Champ>

          {/* Le champ non pertinent est **absent**, pas grisé : un champ grisé
              se remplit mentalement et fait douter de ce qui sera enregistré. */}
          {mode === 'HORAIRE' ? (
            <Champ
              libelle="Taux horaire (€/h)"
              id="taux-horaire"
              erreur={e.taux_horaire?.message}
              obligatoire
            >
              <Saisie id="taux-horaire" type="number" step="0.01" min="0.01" {...register('taux_horaire')} />
            </Champ>
          ) : (
            <>
              <Champ
                libelle="Taux de rétrocession (%)"
                id="taux-retro"
                erreur={e.taux_retrocession?.message}
                aide="Part des honoraires conservée par le cabinet."
                obligatoire
              >
                <Saisie
                  id="taux-retro"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  {...register('taux_retrocession')}
                />
              </Champ>
              <Bandeau ton="info">
                Le relevé d&apos;honoraires calcule le montant dû à l&apos;infirmier après
                déduction de cette part. C&apos;est un relevé, jamais un bulletin de paie.
              </Bandeau>
            </>
          )}
        </section>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-5">
          <Bouton variante="secondaire" type="button" onClick={() => naviguer('/utilisateurs')}>
            Annuler
          </Bouton>
          <Bouton type="submit" disabled={enregistrer.isPending}>
            {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        </div>
      </form>
    </div>
  )
}
